import { Menu } from '@mantine/core';
import {
    IconDots,
    IconFolderShare,
    IconPlayerPlay,
    IconPlayerStop,
    IconReload,
    IconTrash,
} from '@tabler/icons-react';
import { moveTriggerProps } from '@xver/hooks/useMoveResource';
import { useTranslation } from 'react-i18next';
import { BaremetalInstance } from '../../../../../../../@types/instance';

interface BaremetalActionsMenuProps {
    baremetal: BaremetalInstance;
    isLoading: boolean;
    /** False with view-only access: every action mutates, so the menu is not rendered. */
    canManage: boolean;
    canMove: boolean;
    onReboot: () => void;
    onPowerOff: () => void;
    onPowerOn: () => void;
    onMove: () => void;
    onDestroy: () => void;
}

export default function BaremetalActionsMenu({
    baremetal,
    isLoading,
    canManage,
    canMove,
    onReboot,
    onPowerOff,
    onPowerOn,
    onMove,
    onDestroy,
}: BaremetalActionsMenuProps) {
    const { t } = useTranslation('bareMetal');
    const isRunning = baremetal.status === 'running';
    const isStopped = baremetal.status === 'stopped';
    const isInteractable = isRunning || isStopped;

    if (!isInteractable || !canManage) {
        return null;
    }

    return (
        <Menu position="bottom-end" withinPortal>
            <Menu.Target>
                <button
                    {...moveTriggerProps('baremetal', baremetal.id)}
                    type="button"
                    aria-label={t('table.rowActions')}
                    disabled={isLoading}
                    onClick={(e) => e.stopPropagation()}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg/68 hover:bg-surface-3 hover:text-fg transition-colors disabled:opacity-50"
                >
                    <IconDots size={16} />
                </button>
            </Menu.Target>
            <Menu.Dropdown>
                {isRunning && (
                    <Menu.Item
                        color="var(--color-status-warning)"
                        leftSection={<IconReload size={14} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onReboot();
                        }}
                    >
                        {t('menu.reboot')}
                    </Menu.Item>
                )}
                {isRunning && (
                    <Menu.Item
                        color="var(--color-status-danger)"
                        leftSection={<IconPlayerStop size={14} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPowerOff();
                        }}
                    >
                        {t('menu.stop')}
                    </Menu.Item>
                )}
                {isStopped && (
                    <Menu.Item
                        color="var(--color-status-success)"
                        leftSection={<IconPlayerPlay size={14} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onPowerOn();
                        }}
                    >
                        {t('menu.start')}
                    </Menu.Item>
                )}
                {canMove && (
                    <Menu.Item
                        leftSection={<IconFolderShare size={14} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onMove();
                        }}
                    >
                        {t('projects:resource.moveToProject')}
                    </Menu.Item>
                )}
                <Menu.Item
                    color="var(--color-status-danger)"
                    leftSection={<IconTrash size={14} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDestroy();
                    }}
                >
                    {t('menu.destroy')}
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}
